/**
 * @Copyright 2014-2019 DataVoke, Inc. All rights reserved.
 */

'use strict';

import {RecordTypeEntity} from "../entities/recordType";
import {Dictionary} from "../utils/Dictionary";
import {noop} from "../utils/tools";
import {EntityStates} from "../utils/dvtypes";
import {properties as propertyCache} from "./properties";

/**
 * @callback MergeRecordTypeEntityCallback
 * @param {RecordTypeEntity} recordType - The Record Type Entity that was merged
 * @param {string} action - The action that was taken with the merged entity
 */

class RecordTypes {
    constructor () {
        /**
         * Dictionary object for all record type entities
         * @type {Dictionary}
         */
        this.allRecordTypes = new Dictionary();
    }

    /**
     * Merge new or updated property entities with the property cache
     * @param {Array<Object>} entities - Array of entity objects that will become PropertyEntity objects
     * @param {MergeRecordTypeEntityCallback} [newCallback = noop]
     * @param {MergeRecordTypeEntityCallback} [updatedCallback = noop]
     */
    mergeCache (entities, newCallback = noop, updatedCallback = noop) {
        if (!Array.isArray(entities)) {
            return;
        }

        entities.forEach(
            /**
             * Create a Record Type entity from the raw JSON object and populate the cache
             * @param {Object} entity
             */
            (entity) => {
                let recordType = new RecordTypeEntity(entity),
                    cachedRecordType = this.allRecordTypes.getVal(recordType.RecordTypeID);

                if (!cachedRecordType) {
                    // update the state to unmodified - we are getting this from the service, after all...
                    recordType.setState(EntityStates.Unmodified);

                    // this is a new record type, add it to the cache
                    this.allRecordTypes.add(recordType.RecordTypeID, recordType, recordType.UID);

                    newCallback(recordType, 'added');
                }
                else {
                    if (recordType.State === EntityStates.Deleted) {
                        // this is a deleted record type, remove it from the cache
                        this.allRecordTypes.remove(recordType.RecordTypeID);

                        updatedCallback(cachedRecordType, 'removed');
                    }
                    else {
                        // update the existing record type
                        cachedRecordType.Definition = recordType.Definition;
                        cachedRecordType.Serial = recordType.Serial;
                        cachedRecordType.Name = recordType.Name;

                        updatedCallback(cachedRecordType, 'updated');
                    }
                }
            });

    }

    /**
     * Retrieve the record type entity from the service specified by the ID
     * @param {number} ID
     * @returns {*}
     */
    getByRecordTypeID (ID) {
        return new Promise(async (resolve, reject) => {
            //Make the call...EK
            const result = await app.dv.services.call(app.dv.services.endpoints.getRecordTypeByID, {
                Session: app.dv.cache.session.sessionGUID,
                RecordTypeID: ID
            });

            if (result != null) {
                // this will hydrate the returned entity and update in cache
                this.mergeCache([result],
                    /**
                     * This was a new record type - we want to return it.
                     * @param {RecordTypeEntity} recordType
                     */
                    (recordType) => {
                        // return the new record type through the promise
                        resolve(recordType);
                    },
                    /**
                     * This was an existing record type that was updated - we want to return it.
                     * @param {RecordTypeEntity} recordType
                     */
                    (recordType) => {
                        // return the updated record type through the promise
                        resolve(recordType);
                    });
            }
            else {
                // return the value through the promise
                resolve(result);
            }
        });
    }

    /**
     * Get the serial property entity for the specified record type ID, if configured
     * @param {EntityLinker|number} recordTypeID
     * @returns {null|PropertyEntity}
     */
    getSerialPropertyForRT (recordTypeID) {
        const recType = this.allRecordTypes.getVal(recordTypeID);
        if (recType) {
            const serial = recType.Definition.SerialPropertyID;
            if (serial) {
                return propertyCache.allProperties.getVal(serial);
            }
        }
        return null;
    }

    /**
     * Get the record code property entity for the specified record type ID
     * @param {EntityLinker|number} recordTypeID
     * @returns {null|PropertyEntity}
     */
    getCodePropertyForRT (recordTypeID) {
        const recType = this.allRecordTypes.getVal(recordTypeID);
        if (recType) {
            const code = recType.Definition.CodePropertyID;
            if (code) {
                return propertyCache.allProperties.getVal(code);
            }
        }
        return null;
    }
}

export const recordTypes = new RecordTypes();
